import { WidgetUUID } from '@/common/utils';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { GridLayoutItem } from '@/dashboard/shared/types/widgets.types';

type CustomWidgetId = string;

interface CustomWidgetIdInfo {
  customId: CustomWidgetId;
  widgetName: string; // customId가 없을 경우 widgetName을 표시
}

const extractWidgetIdInfo = (gridLayoutItems: GridLayoutItem[]): [string, CustomWidgetIdInfo][] => {
  return gridLayoutItems.flatMap(({ i: widgetUUID, customId, titleOption, children }) => {
    const widgetInfo: CustomWidgetIdInfo = {
      customId: customId ?? '',
      widgetName: titleOption?.titleText ?? '',
    };
    // 자식이 없으면 현재 widgetUUID와 widgetIdInfo만 반환
    if (!children) {
      return [[widgetUUID, widgetInfo]];
    }
    // 자식이 있으면 재귀적으로 처리
    const childCustomWidget = extractWidgetIdInfo(children);
    return [[widgetUUID, widgetInfo], ...childCustomWidget];
  });
};

export const useWidgetCustomIdStore = defineStore('widgetCustomIdStore', () => {
  // 하나의 dashboard에 대한 모든 widget 정보(customId, widgetName)
  const customWidgetIdInfo = ref<Map<WidgetUUID, CustomWidgetIdInfo>>(new Map());

  // dashboard 진입할때마다 업데이트
  const initCustomWidgetIdInfo = (gridLayoutItems: GridLayoutItem[]) => {
    customWidgetIdInfo.value = new Map(extractWidgetIdInfo(gridLayoutItems));
  };

  const getCustomWidgetIdInfo = (widgetUUID: WidgetUUID): CustomWidgetIdInfo | undefined => {
    return customWidgetIdInfo.value.get(widgetUUID);
  };

  const getCustomWidgetLabel = (widgetUUID: WidgetUUID): string => {
    const { customId, widgetName } = getCustomWidgetIdInfo(widgetUUID) ?? {
      customId: '',
      widgetName: '',
    };
    return customId || widgetName;
  };

  const setCustomWidgetIdInfo = (widgetUUID: WidgetUUID, widgetInfo: CustomWidgetIdInfo) => {
    customWidgetIdInfo.value.set(widgetUUID, widgetInfo);
  };

  const deleteCustomWidgetId = (widgetUUID: WidgetUUID) => {
    customWidgetIdInfo.value.delete(widgetUUID);
  };

  const isCustomIdDuplicate = (widgetUUID: WidgetUUID, customId: string) => {
    for (const key of customWidgetIdInfo.value.keys()) {
      if (key !== widgetUUID && getCustomWidgetIdInfo(key)?.customId === customId) {
        return true;
      }
    }
    return false;
  };

  // 사용 불가능한 customWidgetID를 입력한 경우 widgetSettingWindow의 save 버튼 disabled 시키기 위한 변수
  const isCustomWidgetIdError = ref(false);

  const updateIsCustomWidgetIdError = (value: boolean) => {
    isCustomWidgetIdError.value = value;
  };

  return {
    isCustomIdDuplicate,
    getCustomWidgetIdInfo,
    getCustomWidgetLabel,
    setCustomWidgetIdInfo,
    initCustomWidgetIdInfo,
    deleteCustomWidgetId,
    isCustomWidgetIdError,
    updateIsCustomWidgetIdError,
  };
});
